/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { InputNumber, Popover, Select, Switch } from 'antd'
import type { CSSProperties, ReactElement } from 'react'
import { useState } from 'react'

import colors from '@sportninja/common/constants/appColors'
import type { BulkAction } from '@sportninja/common/features/game/gameListView/bulkPlan'
import type { VenueRef } from '@sportninja/common/features/game/gameListView/types'

import { font, weightRegular, weightSemiBold } from 'src/components/css'
import type { GameTypeOption } from '../hooks/useDetailsColumns'
import { gameViewsLabel } from '../utils/i18n'
import { GameDatePicker, GameTimePicker } from './pickers'

export interface BulkToolbarProps {
  selectedCount: number
  venues: VenueRef[]
  gameTypes: GameTypeOption[]
  disabled: boolean
  /**
   * Previews the effect before committing — `planBulkEdit` is pure, so the
   * counts shown here are produced by the exact call that will run.
   */
  preview: (action: BulkAction) => { affected: number; skipped: number }
  onApply: (action: BulkAction) => void
}

type PanelKey = 'date' | 'time' | 'shift' | 'venue' | 'number' | 'type'

const SHIFT_UNITS = ['days', 'hours', 'minutes'] as const
type ShiftUnit = (typeof SHIFT_UNITS)[number]

/*
 * One width for every panel, and every control fills it.
 *
 * The panel used to size itself to its widest child, which was always the
 * "N games selected…" line, while the fields stayed at a fixed 240 — so the
 * calendar, the time list and the selects all floated in a box wider than
 * themselves with dead space down the right-hand side.
 */
const PANEL_WIDTH = 300

/**
 * antd renders picker and select dropdowns in a body-level portal by default,
 * which puts them outside the popover — they get clipped, and a click in them
 * reads as a click outside. Anchoring them to the popover's own inner element
 * keeps them inside it.
 */
const insidePopover = (node: HTMLElement): HTMLElement =>
  (node.closest('.ant-popover-inner') as HTMLElement) ?? document.body

/**
 * Sticky so it stays reachable while the admin scrolls a long selection — the
 * whole complaint was about losing your place, and a toolbar that scrolls away
 * mid-run recreates it.
 */
/**
 * antd `style` props, hoisted out of the JSX.
 *
 * These stay inline STYLE rather than becoming Emotion classes on purpose: antd
 * ships its own width and surface rules for these components, and a class would
 * be arguing with them on specificity. As module constants they are also stable
 * identities, so they never look like a changed prop.
 */
const POPOVER_INNER_STYLE: CSSProperties = {
  background: colors.SECONDARY_400,
  border: `1px solid ${colors.SECONDARY_200}`,
  borderRadius: 10,
  padding: 16,
}

const FULL_WIDTH: CSSProperties = { width: '100%' }

const BulkToolbar = ({
  selectedCount,
  venues,
  gameTypes,
  disabled,
  preview,
  onApply,
}: BulkToolbarProps) => {
  'use memo'

  const [openPanel, setOpenPanel] = useState<PanelKey | null>(null)
  const [date, setDate] = useState<string | null>(null)
  const [time, setTime] = useState<string | null>(null)
  const [timeTbd, setTimeTbd] = useState(false)
  const [shiftAmount, setShiftAmount] = useState<number | null>(1)
  const [shiftUnit, setShiftUnit] = useState<ShiftUnit>('days')
  const [venueId, setVenueId] = useState<string | null>(null)
  const [facilityId, setFacilityId] = useState<string | null>(null)
  const [gameNumber, setGameNumber] = useState('')
  const [gameTypeId, setGameTypeId] = useState<number | null>(null)

  //  Distinct from `disabled`, which is also true mid-run: a running bulk edit
  //  has a selection, and telling the admin to make one would be nonsense.
  const hasNoSelection = selectedCount === 0

  const close = () => setOpenPanel(null)

  const apply = (action: BulkAction | null) => {
    if (action === null) {
      return
    }
    onApply(action)
    close()
  }

  const shiftAction = (): BulkAction | null => {
    if (
      shiftAmount === null ||
      !Number.isFinite(shiftAmount) ||
      shiftAmount === 0
    ) {
      return null
    }
    if (shiftUnit === 'days') {
      //  Days are LOCAL calendar days; hours and minutes are absolute. Across a
      //  DST boundary those differ, and a game moved by 1440 minutes lands an
      //  hour off its slot.
      return { kind: 'shiftLocalDays', days: shiftAmount }
    }
    return {
      kind: 'shiftMinutes',
      minutes: shiftUnit === 'hours' ? shiftAmount * 60 : shiftAmount,
    }
  }

  const facilities =
    venues.find((venue) => venue.id === venueId)?.facilities ?? []

  /**
   * Every panel is the same shape: a title, a labelled field, then a line that
   * ALWAYS says what Apply will do. The summary is never hidden while the field
   * is empty — a popover that shows nothing until you guess right is what made
   * the first version unusable.
   */
  const panel = (
    titleKey: string,
    body: ReactElement,
    action: () => BulkAction | null
  ) => {
    const candidate = action()
    const counts = candidate === null ? null : preview(candidate)
    const canApply = candidate !== null && (counts?.affected ?? 0) > 0

    return (
      <div css={panelStyles}>
        <p css={panelTitleStyles}>{gameViewsLabel(titleKey)}</p>
        {body}
        <p css={previewStyles} role='status'>
          {counts === null
            ? gameViewsLabel('bulkPreviewPending', { count: selectedCount })
            : gameViewsLabel('bulkPreview', {
                affected: counts.affected,
                skipped: counts.skipped,
              })}
        </p>
        <div css={panelActionsStyles}>
          <button type='button' css={secondaryButtonStyles} onClick={close}>
            {gameViewsLabel('bulkCancel')}
          </button>
          <button
            type='button'
            css={primaryButtonStyles}
            disabled={!canApply}
            onClick={() => apply(candidate)}
          >
            {gameViewsLabel('bulkApply')}
          </button>
        </div>
      </div>
    )
  }

  const field = (labelKey: string, control: ReactElement) => (
    <label css={fieldStyles}>
      <span css={fieldLabelStyles}>{gameViewsLabel(labelKey)}</span>
      {control}
    </label>
  )

  const trigger = (key: PanelKey, labelKey: string, content: ReactElement) => (
    <Popover
      key={key}
      open={openPanel === key}
      onOpenChange={(open) => setOpenPanel(open ? key : null)}
      trigger='click'
      placement='bottomLeft'
      arrow={false}
      destroyTooltipOnHide
      overlayInnerStyle={POPOVER_INNER_STYLE}
      content={content}
    >
      <button
        type='button'
        css={triggerStyles(openPanel === key)}
        disabled={disabled}
        /*
         * A disabled control that will not say why reads as broken — this one
         * was reported as one. `title` is deliberate: it is the only tooltip a
         * DISABLED button still shows, because it needs no pointer events.
         */
        title={
          hasNoSelection ? gameViewsLabel('bulkNeedsSelection') : undefined
        }
        aria-haspopup='dialog'
        aria-expanded={openPanel === key}
        data-testid={`bulk-${key}`}
      >
        {gameViewsLabel(labelKey)}
      </button>
    </Popover>
  )

  return (
    <div
      css={barStyles}
      role='toolbar'
      aria-label={gameViewsLabel('bulkToolbarLabel')}
    >
      {/*
        "0 selected" beside six greyed-out buttons states a fact and answers
        nothing — the bar has to say what it is waiting for. Once there IS a
        selection the count is the useful half, so the two swap.
      */}
      <span
        css={[countStyles, hasNoSelection && promptStyles]}
        data-testid='bulk-selection-state'
      >
        {hasNoSelection
          ? gameViewsLabel('bulkSelectPrompt')
          : gameViewsLabel('bulkSelectedCount', { count: selectedCount })}
      </span>

      {trigger(
        'date',
        'bulkSetDate',
        panel(
          'bulkSetDate',
          field(
            'gameGridEditDate',
            <GameDatePicker
              value={date}
              onChange={setDate}
              getPopupContainer={insidePopover}
            />
          ),
          () => (date === null ? null : { kind: 'setLocalDate', date })
        )
      )}

      {trigger(
        'time',
        'bulkSetTime',
        panel(
          'bulkSetTime',
          <>
            {field(
              'gameGridEditTime',
              <GameTimePicker
                value={timeTbd ? null : time}
                onChange={setTime}
                disabled={timeTbd}
                placeholder={
                  timeTbd ? gameViewsLabel('gameGridTbd') : undefined
                }
                getPopupContainer={insidePopover}
              />
            )}
            {/*
              A plain row, NOT `field()` — that helper wraps its control in a
              <label>, which would give this <button role="switch"> a second,
              implicit accessible name on top of its own.

              No `getPopupContainer`: a Switch opens no portal. It still must
              not read as a click outside, which it does not — it is a child of
              the popover's own content.
            */}
            <div css={toggleRowStyles}>
              <Switch
                size='small'
                checked={timeTbd}
                aria-label={gameViewsLabel('gameGridEditTimeTbd')}
                onChange={setTimeTbd}
              />
              <span css={fieldLabelStyles}>
                {gameViewsLabel('gameGridEditTimeTbd')}
              </span>
            </div>
          </>,
          () =>
            timeTbd
              ? { kind: 'setTbd' }
              : time === null
              ? null
              : { kind: 'setLocalTime', time }
        )
      )}

      {trigger(
        'shift',
        'bulkShiftBy',
        panel(
          'bulkShiftBy',
          <div css={rowStyles}>
            {field(
              'bulkShiftAmount',
              <InputNumber
                value={shiftAmount}
                //  Whole units only. Luxon accepts 1.5 days and formatting the
                //  result back to a civil date silently lands it on a different
                //  day than the one asked for — and a negative fraction moves a
                //  different number of days again.
                precision={0}
                step={1}
                parser={(text) => {
                  const parsed = Number.parseInt(text ?? '', 10)
                  return Number.isNaN(parsed) ? 0 : parsed
                }}
                onChange={(next) =>
                  setShiftAmount(
                    next == null ? null : Math.trunc(next as number)
                  )
                }
                style={FULL_WIDTH}
              />
            )}
            {field(
              'bulkShiftUnit',
              <Select
                value={shiftUnit}
                onChange={(value: ShiftUnit) => setShiftUnit(value)}
                options={SHIFT_UNITS.map((unit) => ({
                  value: unit,
                  label: gameViewsLabel(`bulkShiftUnit_${unit}`),
                }))}
                style={FULL_WIDTH}
                getPopupContainer={insidePopover}
              />
            )}
          </div>,
          shiftAction
        )
      )}

      {trigger(
        'venue',
        'bulkSetVenue',
        panel(
          'bulkSetVenue',
          <div css={columnStyles}>
            {field(
              'gameGridColVenue',
              <Select
                value={venueId}
                onChange={(value: string) => {
                  setVenueId(value)
                  setFacilityId(null)
                }}
                options={venues.map((venue) => ({
                  value: venue.id,
                  label: venue.name_full ?? venue.name ?? venue.id,
                }))}
                style={FULL_WIDTH}
                showSearch
                optionFilterProp='label'
                placeholder={gameViewsLabel('bulkChooseVenue')}
                getPopupContainer={insidePopover}
              />
            )}
            {field(
              'gameGridColFacility',
              <Select
                value={facilityId}
                onChange={(value: string) => setFacilityId(value)}
                options={facilities.map((facility) => ({
                  value: facility.id,
                  label: facility.name ?? facility.id,
                }))}
                disabled={venueId === null}
                style={FULL_WIDTH}
                //  Same rule as the toolbar's own buttons: a disabled control
                //  says what it is waiting for. Surfaces belong to a location,
                //  so there is nothing to list until one is picked.
                placeholder={gameViewsLabel(
                  venueId === null
                    ? 'bulkFacilityNeedsVenue'
                    : 'bulkChooseFacility'
                )}
                getPopupContainer={insidePopover}
              />
            )}
          </div>,
          () =>
            venueId === null ? null : { kind: 'setVenue', venueId, facilityId }
        )
      )}

      {trigger(
        'number',
        'bulkSetGameNumber',
        panel(
          'bulkSetGameNumber',
          field(
            'gameGridColGameNumber',
            <input
              css={textInputStyles}
              type='text'
              value={gameNumber}
              onChange={(event) => setGameNumber(event.target.value)}
            />
          ),
          () => ({ kind: 'setGameNumber', value: gameNumber })
        )
      )}

      {trigger(
        'type',
        'bulkSetGameType',
        panel(
          'bulkSetGameType',
          field(
            'gameGridColGameType',
            <Select
              value={gameTypeId}
              onChange={(value: number) => setGameTypeId(value)}
              options={gameTypes.map((type) => ({
                value: Number(type.id),
                label: type.name_full ?? type.name ?? String(type.id),
              }))}
              style={FULL_WIDTH}
              placeholder={gameViewsLabel('bulkChooseGameType')}
              getPopupContainer={insidePopover}
            />
          ),
          () =>
            gameTypeId === null ? null : { kind: 'setGameType', gameTypeId }
        )
      )}
    </div>
  )
}

// ─── Styles ──────────────────────────────────────────────────────

const barStyles = css`
  ${font.body}
  position: sticky;
  top: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 12px;
  margin-bottom: 12px;
  background: ${colors.SECONDARY_400};
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 8px;
`

const countStyles = css`
  ${weightSemiBold}
  font-size: 13px;
  color: ${colors.NEUTRAL_0};
  margin-right: 8px;
`

const promptStyles = css`
  ${weightRegular}
  color: ${colors.NEUTRAL_50};
`

const triggerStyles = (isOpen: boolean) => css`
  ${font.body}
  height: 32px;
  padding: 0 12px;
  border-radius: 6px;
  border: 1px solid ${isOpen ? colors.PRIMARY : colors.SECONDARY_100};
  background: ${isOpen ? colors.SECONDARY_300 : 'transparent'};
  color: ${colors.NEUTRAL_0};
  font-size: 13px;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${colors.SECONDARY_300};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  &:focus-visible {
    outline: 2px solid ${colors.PRIMARY};
    outline-offset: 1px;
  }
`

const panelStyles = css`
  ${font.body}
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: ${PANEL_WIDTH}px;
`

const panelTitleStyles = css`
  ${weightSemiBold}
  margin: 0;
  font-size: 13px;
  color: ${colors.NEUTRAL_0};
`

const rowStyles = css`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`

const columnStyles = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const fieldStyles = css`
  display: flex;
  flex-direction: column;
  gap: 6px;
  //  min-width 0 so a field sharing a row can actually shrink — without it a
  //  flex item refuses to go below its content width and overflows the panel.
  flex: 1;
  min-width: 0;
`

const fieldLabelStyles = css`
  font-size: 12px;
  color: ${colors.NEUTRAL_50};
`

/** The switch and its text on one line, under the field it modifies. */
const toggleRowStyles = css`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
`

const textInputStyles = css`
  ${font.body}
  width: 100%;
  height: 32px;
  padding: 0 11px;
  border-radius: 6px;
  border: 1px solid ${colors.SECONDARY_100};
  background: ${colors.SECONDARY_500};
  color: ${colors.NEUTRAL_0};
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: ${colors.PRIMARY};
  }
`

const previewStyles = css`
  font-size: 12px;
  margin: 0;
  padding-top: 12px;
  border-top: 1px solid ${colors.SECONDARY_200};
  color: ${colors.NEUTRAL_50};
`

const panelActionsStyles = css`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`

const secondaryButtonStyles = css`
  ${font.body}
  height: 32px;
  padding: 0 14px;
  border-radius: 6px;
  border: 1px solid ${colors.SECONDARY_100};
  background: transparent;
  color: ${colors.NEUTRAL_0};
  cursor: pointer;

  &:hover {
    background: ${colors.SECONDARY_300};
  }
`

const primaryButtonStyles = css`
  ${font.body}
  ${weightSemiBold}
  height: 32px;
  padding: 0 14px;
  border-radius: 6px;
  border: none;
  background: ${colors.PRIMARY};
  color: ${colors.PRIMARY_TEXT};
  cursor: pointer;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`

export default BulkToolbar
